import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Setup } from '../../../setup/shared/service/setup';
import { CraditService } from '../../shared/services/cradit.service';
import { PickListService } from '../../../../core/shared/services/picklist.service';
import { PKL_GRP, QSTNR_SEQ } from '../../../../core/shared/models/picklist.model';
import { ToastService } from '../../../../core/shared/services/toast.service';
import {
  CadResponseDto,
  CovenantAnswerOption,
  CovenantResponseDto,
  CovenantRow,
  SaveCadResponseItem,
  SaveCovenantResponseItem,
} from '../../shared/models/proposed-disbursement.model';

/**
 * Covenant compliance grid of the Disbursement Note.
 *
 * Questions and their answer options are setup-driven — the covenant list is
 * questionnaire 5 in `/setup/qstnr/*`, so adding or rewording a covenant is a
 * setup change, not a code change. Saved responses come back from
 * `/crdt-prpsl/get-covenant-responses/{crdtPrpslSeq}` and are re-selected here.
 */
@Component({
  selector: 'app-covenant-compliance',
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './covenant-compliance.html',
  styleUrl: './covenant-compliance.css',
})
export class CovenantCompliance implements OnChanges {
  /** Responses are stored against the credit proposal, not the disbursement. */
  @Input() crdtPrpslSeq = 0;

  /** Questionnaire holding the covenant list. */
  @Input() qstnrSeq: number = QSTNR_SEQ.COVENANT;

  /** Off when the host page drives saving from its own action bar. */
  @Input() showSaveButton = true;

  /** Shown when the questionnaire has no active questions. */
  @Input() emptyMessage = 'DISB_NOTE.NO_COVENANTS_CONFIGURED';

  /**
   * 'covenant' saves to `/crdt-prpsl/save-covenant-responses` against the credit
   * proposal; 'cad' saves to `/dsbmnt-note/save-cad-rsp` against the disbursement.
   * In 'cad' mode `crdtCnvntRspSeq` on each row holds the `cadRspSeq`.
   */
  @Input() mode: 'covenant' | 'cad' = 'covenant';

  /** CAD responses are stored against the proposed disbursement. */
  @Input() prpsdDsbmntSeq = 0;

  /** View mode: answers are shown but cannot be edited or saved. */
  @Input() readOnly = false;

  /** Covenant mode only: shows the Comment column. */
  @Input() showComment = true;

  constructor(
    private setup: Setup,
    private creditService: CraditService,
    private pickListService: PickListService,
    private toast: ToastService,
  ) {}

  rows: CovenantRow[] = [];
  loading = false;
  loadError = '';
  saving = false;

  /** Saved answers that matched no option in setup — surfaced instead of dropped. */
  orphanCount = 0;

  private questionTypeMap: Record<number, string> = {};

  /** Ref group 46 labels for `stsKey`, the status Monitoring saves on a covenant response. */
  private covenantStatusMap: Record<number, string> = {};

  /** Each covenant row's state as last loaded, keyed by qstSeq, so save sends only edited rows. */
  private savedState = new Map<number, string>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['crdtPrpslSeq'] && this.crdtPrpslSeq > 0) {
      this.load();
    }
  }

  // ================= LOAD =================
  private load(): void {
    this.loading = true;
    this.loadError = '';

    // Question types drive whether a covenant renders as a dropdown or a text box.
    this.pickListService.loadCodesAsync([PKL_GRP.QUESTION_TYPE, PKL_GRP.COVENANT_STATUS]).subscribe({
      next: () => {
        this.questionTypeMap = {};
        this.covenantStatusMap = {};

        for (const option of this.pickListService.getOptionsSync(PKL_GRP.COVENANT_STATUS)) {
          this.covenantStatusMap[Number(option.value)] = String(option.label ?? '');
        }

        for (const option of this.pickListService.getOptionsSync(PKL_GRP.QUESTION_TYPE)) {
          this.questionTypeMap[Number(option.value)] = String(
            option.original?.refCdDscr ?? option.label ?? '',
          );
        }

        this.loadQuestions();
      },

      error: () => this.loadQuestions(),
    });
  }

  /**
   * Covenants grid lists only the covenants selected on the credit proposal
   * (`/crdt-prpsl/get-covenant-questions/{crdtPrpslSeq}`). That endpoint returns
   * just the selected `qstnSeq`s, so the wording still comes from the
   * questionnaire. A proposal with no saved selection keeps the full list
   * rather than emptying the grid.
   */
  private loadQuestions(): void {
    const selections$ =
      this.mode === 'covenant'
        ? this.creditService.getCovenantQuestions(this.crdtPrpslSeq).pipe(
            catchError((error: any) => {
              console.error('Unable to load selected covenants:', error);
              return of([]);
            }),
          )
        : of([]);

    forkJoin({
      response: this.setup.getQuestionList(this.qstnrSeq),
      selections: selections$,
    }).subscribe({
      next: ({ response, selections }: { response: any; selections: any }) => {
        const selectedSeqs = new Set(
          (Array.isArray(selections) ? selections : [])
            .filter((selected: any) => Number(selected?.delFlg ?? 0) !== 1)
            .map((selected: any) => Number(selected?.qstnSeq)),
        );

        const questions: any[] = (Array.isArray(response) ? response : []).filter(
          (item) => !item?.delFlg && (!selectedSeqs.size || selectedSeqs.has(Number(item.qstSeq))),
        );

        if (!questions.length) {
          this.rows = [];
          this.loading = false;
          return;
        }

        // `qstSortOrdr` is null across the covenant set, so fall back to qstSeq
        // to keep the printed order stable.
        questions.sort(
          (first, second) =>
            Number(first.qstSortOrdr ?? first.qstSeq) - Number(second.qstSortOrdr ?? second.qstSeq),
        );

        const answerCalls = questions.map((question) =>
          this.setup.getAnswerList(question.qstSeq).pipe(catchError(() => of([]))),
        );

        forkJoin(answerCalls).subscribe({
          next: (answersPerQuestion: any[][]) => {
            this.rows = questions.map((question, index) => {
              const options: CovenantAnswerOption[] = (answersPerQuestion[index] || [])
                .filter((answer: any) => !answer?.delFlg)
                .map((answer: any) => ({
                  answrSeq: Number(answer.answrSeq),
                  answrStr: String(answer.answrStr ?? ''),
                }));

              return {
                qstSeq: Number(question.qstSeq),

                // Setup rows carry trailing tabs/newlines from the source document.
                question: String(question.qstStr ?? '').trim(),

                inputType: this.resolveInputType(Number(question.qstTypKey), options.length),

                options,

                ansVal: '',
                rmrks: '',
                cmnt: '',
                lastUpdDt: null,
                stsKey: null,
                crdtCnvntRspSeq: null,
              };
            });

            this.loadSavedResponses();
          },

          error: (error: any) => {
            console.error('Unable to load covenant answers:', error);

            this.rows = [];
            this.loading = false;
            this.loadError = 'DISB_NOTE.ERR_LOAD_COVENANT_OPTIONS';
          },
        });
      },

      error: (error: any) => {
        console.error('Unable to load covenant questions:', error);

        this.rows = [];
        this.loading = false;
        this.loadError = 'DISB_NOTE.ERR_LOAD_COVENANT_LIST';
      },
    });
  }

  private loadSavedResponses(): void {
    if (this.mode === 'cad') {
      this.loadSavedCadResponses();
      return;
    }

    this.creditService.getCovenantResponses(this.crdtPrpslSeq).subscribe({
      next: (response: any) => {
        const saved = this.toSavedResponses(response);

        if (!saved.length) {
          console.warn('get-covenant-responses returned no saved rows for proposal', this.crdtPrpslSeq, response);
        }

        this.applySavedResponses(saved);
        this.snapshotRows();
        this.loading = false;
      },

      error: (error: any) => {
        console.error('Unable to load covenant responses:', error);

        // The grid is still usable unanswered, so this is not a hard failure.
        this.snapshotRows();
        this.loading = false;
        this.loadError = 'DISB_NOTE.ERR_LOAD_COVENANT_RESPONSES';
      },
    });
  }

  /**
   * Saved covenant rows from a get/save response: a bare array or one wrapped
   * in `data` / `responses`; the question seq may be `qsntnSeq` or `qstnSeq`.
   */
  private toSavedResponses(response: any): CovenantResponseDto[] {
    const list: any[] = Array.isArray(response)
      ? response
      : Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.responses)
          ? response.responses
          : [];

    return list
      .filter((item) => Number(item?.delFlg ?? 0) !== 1)
      .map((item) => ({ ...item, qsntnSeq: Number(item?.qsntnSeq ?? item?.qstnSeq) }));
  }

  private rowState(row: CovenantRow): string {
    return JSON.stringify([
      String(row.ansVal ?? '').trim(),
      String(row.rmrks ?? '').trim(),
      String(row.cmnt ?? '').trim(),
    ]);
  }

  private snapshotRows(): void {
    this.savedState = new Map(this.rows.map((row) => [row.qstSeq, this.rowState(row)]));
  }

  /** A row the user edited or selected since it was last loaded. */
  private isRowChanged(row: CovenantRow): boolean {
    return this.savedState.get(row.qstSeq) !== this.rowState(row);
  }

  /**
   * Keeps each row's `crdtCnvntRspSeq` from the save response, so the next
   * save updates those rows even if the reload does not return them.
   */
  private applySavedSeqs(saved: CovenantResponseDto[]): void {
    for (const response of saved) {
      const row = this.rows.find((item) => item.qstSeq === Number(response.qsntnSeq));

      if (row && response.crdtCnvntRspSeq != null) {
        row.crdtCnvntRspSeq = Number(response.crdtCnvntRspSeq);
      }
    }
  }

  /**
   * Saved CAD rows come back as `{ cadRspSeq, qstnSeq, answrVal, rmrks }` and
   * are mapped onto the covenant shape so the same re-select logic patches them.
   */
  private loadSavedCadResponses(): void {
    if (this.prpsdDsbmntSeq <= 0) {
      this.loading = false;
      return;
    }

    this.creditService.getCadResponses(this.prpsdDsbmntSeq).subscribe({
      next: (response: any) => {
        const list: CadResponseDto[] = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : [];

        const saved: CovenantResponseDto[] = list
          .filter((item) => Number(item?.delFlg ?? 0) !== 1)
          .map((item) => ({
            crdtCnvntRspSeq: Number(item.cadRspSeq),
            crdtPrpslSeq: this.crdtPrpslSeq,
            qsntnSeq: Number(item.qstnSeq),
            ansVal: item.answrVal ?? '',
            rmrks: item.rmrks ?? '',
            delFlg: 0,
          }));

        // A row deleted on the last save no longer comes back, so its seq is
        // cleared before the saved ones are re-applied.
        this.rows.forEach((row) => (row.crdtCnvntRspSeq = null));

        this.applySavedResponses(saved);
        this.loading = false;
      },

      error: (error: any) => {
        console.error('Unable to load CAD responses:', error);

        this.loading = false;
        this.loadError = 'DISB_NOTE.ERR_LOAD_CAD_RESPONSES';
      },
    });
  }

  /**
   * Re-selects saved answers. `ansVal` is matched against the option text first
   * and the answer sequence second, because either may have been stored. A value
   * that matches neither is kept as a flagged option rather than silently reset,
   * so a saved response is never lost by re-saving the form.
   */
  private applySavedResponses(saved: CovenantResponseDto[]): void {
    this.orphanCount = 0;

    for (const response of saved) {
      const row = this.rows.find((item) => item.qstSeq === Number(response.qsntnSeq));

      if (!row) {
        continue;
      }

      row.crdtCnvntRspSeq =
        response.crdtCnvntRspSeq != null ? Number(response.crdtCnvntRspSeq) : null;

      row.rmrks = response.rmrks ?? '';
      row.cmnt = response.cmnt ?? '';
      row.lastUpdDt = response.lastUpdDt ?? null;
      row.stsKey = Number(response.stsKey) > 0 ? Number(response.stsKey) : null;

      const savedValue = String(response.ansVal ?? '').trim();

      if (!savedValue) {
        continue;
      }

      // Free-text answers have no option list to match against.
      if (row.inputType === 'text') {
        row.ansVal = savedValue;
        continue;
      }

      const match = this.findOption(row.options, savedValue);

      if (match) {
        row.ansVal = match.answrStr;
        continue;
      }

      const orphan: CovenantAnswerOption = {
        answrSeq: -1,
        answrStr: savedValue,
        isOrphan: true,
      };

      row.options = [...row.options, orphan];
      row.ansVal = savedValue;

      this.orphanCount++;
    }
  }

  private findOption(
    options: CovenantAnswerOption[],
    savedValue: string,
  ): CovenantAnswerOption | undefined {
    const normalized = savedValue.toLowerCase();

    return (
      options.find((option) => option.answrStr.trim().toLowerCase() === normalized) ??
      options.find((option) => String(option.answrSeq) === savedValue)
    );
  }

  /**
   * Text/input questions get a text box. Every other type (radio, checkbox,
   * select, dropdown or unmapped) gets a dropdown of its setup answers — unless
   * setup gave it no answers, since an empty dropdown can't be answered.
   */
  private resolveInputType(qstTypKey: number, optionCount: number): string {
    const label = (this.questionTypeMap[qstTypKey] || '').toLowerCase();

    if (label.includes('text') || label.includes('input')) {
      return 'text';
    }

    return optionCount > 0 ? 'select' : 'text';
  }

  // ================= PROGRESS =================
  get answeredCount(): number {
    return this.rows.filter((row) => this.complianceStatus(row) !== '').length;
  }

  /**
   * Compliance Status shown in covenant mode: the proposal's answer (`ansVal`),
   * else the status Monitoring saved (`stsKey`, ref group 46).
   */
  complianceStatus(row: CovenantRow): string {
    const answer = String(row.ansVal ?? '').trim();

    if (answer || row.stsKey == null) {
      return answer;
    }

    return this.covenantStatusMap[row.stsKey] ?? '';
  }

  get allAnswered(): boolean {
    return this.rows.length > 0 && this.answeredCount === this.rows.length;
  }

  isOrphanValue(row: CovenantRow): boolean {
    return row.options.some((option) => option.isOrphan && option.answrStr === row.ansVal);
  }

  /**
   * The dropdown binds the option's display text (`answrStr`), but
   * `save-covenant-responses` expects the option's ref code (`answrSeq`) as
   * `ansVal` — same as the credit proposal's Covenants row. Free-text answers
   * and orphan values (no setup option to map to) are sent as-is.
   */
  private resolveAnswerCode(row: CovenantRow): number | string | null {
    const answer = String(row.ansVal ?? '').trim();

    if (!answer) {
      return null;
    }

    if (row.inputType === 'text') {
      return answer;
    }

    const match = row.options.find((option) => !option.isOrphan && option.answrStr === row.ansVal);

    return match ? match.answrSeq : answer;
  }

  // ================= SAVE =================
  save(): void {
    if (this.mode === 'cad') {
      this.saveCad();
      return;
    }

    if (this.crdtPrpslSeq <= 0) {
      this.toast.error('DISB_NOTE.ERR_PROPOSAL_SEQ_UNAVAILABLE');
      return;
    }

    if (this.saving) {
      return;
    }

    const filledRows = this.rows.filter(
      (row) =>
        String(row.ansVal ?? '').trim() !== '' ||
        String(row.rmrks ?? '').trim() !== '' ||
        String(row.cmnt ?? '').trim() !== '',
    );

    if (!filledRows.length) {
      this.toast.error('DISB_NOTE.ERR_ANSWER_AT_LEAST_ONE');
      return;
    }

    // Only rows edited since the last load are sent; untouched saved rows (e.g.
    // an earlier quarter's) are rejected by the backend if re-sent.
    const responses: SaveCovenantResponseItem[] = filledRows
      .filter((row) => this.isRowChanged(row))
      .map((row) => ({
        crdtCnvntRspSeq: row.crdtCnvntRspSeq,
        qsntnSeq: row.qstSeq,
        ansVal: this.resolveAnswerCode(row),
        // Sent back as loaded so a comment save keeps Monitoring's status.
        stsKey: row.stsKey,
        rmrks: row.rmrks,
        cmnt: row.cmnt,
        delFlg: 0,
      }));

    if (!responses.length) {
      this.toast.error('DISB_NOTE.NO_COVENANT_CHANGES');
      return;
    }

    this.saving = true;

    this.creditService
      .saveCovenantResponses({ crdtPrpslSeq: this.crdtPrpslSeq, responses })
      .subscribe({
        next: (response: any) => {
          this.saving = false;

          if (response?.error) {
            this.toast.error(response.error);
            return;
          }

          this.toast.success(response?.message || 'DISB_NOTE.COVENANT_SAVED');

          // Take the seqs the save returned, then reload so newly created rows
          // pick up their crdtCnvntRspSeq.
          this.applySavedSeqs(this.toSavedResponses(response));
          this.loadSavedResponses();
        },

        error: (error: any) => {
          console.error('Unable to save covenant responses:', error);

          this.saving = false;

          this.toast.error(
            error?.error?.message || error?.error?.error || 'DISB_NOTE.ERR_SAVE_COVENANT',
          );
        },
      });
  }

  /**
   * Answered rows are created (seq null) or updated (saved seq). A previously
   * saved row the user has cleared — no answer and no remarks — is sent with
   * delFlg 1 so the backend deletes it.
   */
  private saveCad(): void {
    if (this.prpsdDsbmntSeq <= 0) {
      this.toast.error('DISB_NOTE.ERR_SEQ_MISSING');
      return;
    }

    if (this.saving) {
      return;
    }

    const payload: SaveCadResponseItem[] = [];

    for (const row of this.rows) {
      const answer = String(row.ansVal ?? '').trim();
      const remarks = String(row.rmrks ?? '').trim();
      const isEmpty = !answer && !remarks;

      if (isEmpty && row.crdtCnvntRspSeq == null) {
        continue;
      }

      payload.push({
        cadRspSeq: row.crdtCnvntRspSeq,
        prpsdDsbmntSeq: this.prpsdDsbmntSeq,
        qstnSeq: row.qstSeq,
        answrVal: answer,
        rmrks: remarks,
        delFlg: isEmpty ? 1 : 0,
      });
    }

    if (!payload.length) {
      this.toast.error('DISB_NOTE.ERR_ANSWER_AT_LEAST_ONE');
      return;
    }

    this.saving = true;

    this.creditService.saveCadResponses(payload).subscribe({
      next: (response: any) => {
        this.saving = false;

        if (response?.error) {
          this.toast.error(response.error);
          return;
        }

        this.toast.success(response?.message || 'DISB_NOTE.CAD_SAVED');

        // Reload so newly created rows pick up their cadRspSeq.
        this.loadSavedCadResponses();
      },

      error: (error: any) => {
        console.error('Unable to save CAD responses:', error);

        this.saving = false;

        this.toast.error(
          error?.error?.message || error?.error?.error || 'DISB_NOTE.ERR_SAVE_CAD',
        );
      },
    });
  }
}
